import { createSelector } from "reselect";
import { convertToObjectsArray } from "@shared/utils/arrays";
import { marketplaceFilters } from "@shared/slices/filters.slice";
import { MarketplaceSubGroup } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { filterMarketplaceGroups } from "@shared/features/newMarketplace/utils";

const selectMarketplaceData = (isFullList?: boolean) =>
  createSelector(
    sharedLib.sharedApi.marketplace.endpoints.getMarketplaceInfo.select(),
    sharedLib.sharedApi.marketplace.endpoints.getMarketplaceInfoFull.select(),
    (marketplaceInfo, fullMarketplaceInfo) => {
      return isFullList ? fullMarketplaceInfo : marketplaceInfo;
    }
  );

const selectSubgroups = (isFullList?: boolean) =>
  createSelector(
    selectMarketplaceData(isFullList),
    ({ data: marketplaceInfo }): MarketplaceSubGroup[] => {
      if (marketplaceInfo) {
        const { data, headers } = marketplaceInfo;
        const formattedGroupsData = convertToObjectsArray(data, headers);
        return formattedGroupsData.filter(
          (group: MarketplaceSubGroup) => !!group.backtesting_score
        );
      }
      return [];
    }
  );

export const selectMarketplaceLoadingStates = (isFullList?: boolean) =>
  createSelector(selectMarketplaceData(isFullList), ({ status }) => ({
    loading: status === QueryStatus.pending,
    loaded: status === QueryStatus.fulfilled,
  }));

const selectTopTraders = (isFullList?: boolean) =>
  createSelector(selectSubgroups(isFullList), (groups) => {
    return groups
      .slice()
      .sort((a, b) => b.backtesting_score - a.backtesting_score)
      .slice(0, 3);
  });

const selectFilteredSubGroupsData = (isFullList?: boolean) =>
  createSelector(
    selectSubgroups(isFullList),
    marketplaceFilters.selectors.selectFiltersData,
    (groups, filtersData) => {
      return filterMarketplaceGroups(groups, filtersData);
    }
  );

export const marketplaceSelectors = {
  selectSubgroups,
  selectMarketplaceLoadingStates,
  selectFilteredSubGroupsData,
  selectTopTraders,
};
